import { Show, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { useMenu } from '~/components/layouts/Menu/MenuContext';
import { useIsMobileWidth } from '~/hooks/useIsMobileWidth';
import { tutorialRepository } from '~/repositories/tutorialRepository';
import { useLockBodyScroll } from '~/utils/hooks';
import { debounce } from '~/utils/tool';
import { useSearch } from './MagicSearchContext';
import { MagicSearchDropdown } from './components/MagicSearchDropdown';
import { MagicSearchInput } from './components/MagicSearchInput';
import { YouTubeVideoModal } from './components/YouTubeVideoModal';
import type { SearchResult } from './MagicSearchContext';
import type { TutorialDto } from '~/types/tutorial';

/** Matches the `gap-2` between the nav buttons, so the bar sits on the same rhythm. */
const SEARCH_BAR_GAP = 8;

interface SearchBarProps {
  setIsSearchActive: (isSearchActive: boolean) => void;
  /** Measured distance from the viewport's right edge to the nav buttons beside the bar. */
  rightClusterInset: number;
}

const SearchBar = (props: SearchBarProps) => {
  const isMobileWidth = useIsMobileWidth();
  const menu = useMenu();

  const { searchResults, performSearch, applyNavigationResults, resetSearchResults, isLoading, loadMore, hasMore } = useSearch();

  const [searchTerm, setSearchTerm] = createSignal<string | undefined>('');
  const [dropdownVisible, setDropdownVisible] = createSignal<boolean>(false);
  const [formElement, setFormElement] = createSignal<HTMLFormElement | null>(null);
  const [isTyping, setIsTyping] = createSignal<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = createSignal<boolean>(true);
  const [videoModalVisible, setVideoModalVisible] = createSignal<boolean>(false);
  const [selectedTutorial, setSelectedTutorial] = createSignal<TutorialDto | null>(null);
  let dropdownRef: HTMLDivElement | undefined;

  useLockBodyScroll(dropdownVisible);

  const handleOutsideClick = (event: MouseEvent) => {
    const form = formElement();
    if (form && !form.contains(event.target as Node) && dropdownRef && !dropdownRef.contains(event.target as Node)) {
      setDropdownVisible(false);
      props.setIsSearchActive(false);
      if (!searchTerm()) {
        resetSearchResults();
      }
      form.querySelector('input')?.blur();
    }
  };

  onMount(() => {
    document.addEventListener('mousedown', handleOutsideClick);
  });

  onCleanup(() => {
    document.removeEventListener('mousedown', handleOutsideClick);
  });

  const debouncedSearch = debounce(async (term: string) => {
    if (term.trim() !== '') {
      await performSearch(term);
    } else {
      resetSearchResults();
    }
    setIsTyping(false);
    setIsFirstLoad(false);
  }, 1000);

  const handleSearchChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    setSearchTerm(target.value);
    setIsTyping(true);
    if (target.value) {
      applyNavigationResults(target.value);
      debouncedSearch(target.value);
      props.setIsSearchActive(true);
      setDropdownVisible(true);
    } else {
      setDropdownVisible(false);
      props.setIsSearchActive(false);
      resetSearchResults();
      setIsTyping(false);
      setIsFirstLoad(true);
    }
  };

  const handleFocus = () => {
    if (!dropdownVisible()) {
      setDropdownVisible(true);
      props.setIsSearchActive(true);
    }
  };

  const handleKeyDown = (event: KeyboardEvent, ref: HTMLInputElement) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const term = searchTerm()?.trim() || '';
      if (term !== '') {
        performSearch(term);
        setDropdownVisible(true);
      }
      ref.blur();
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setDropdownVisible(false);
    props.setIsSearchActive(false);
    resetSearchResults();
    setIsFirstLoad(true);
    formElement()?.querySelector('input')?.blur();
  };

  const getResultHref = (result: SearchResult): string => {
    switch (result.type) {
      case 'navigation':
        return result.response.path;
      case 'lease':
        return `/leasing/leases/${result.id}`;
      case 'property':
        return `/portfolios/${result.response.portfolioId}/properties/${result.id}`;
      case 'unit':
        return `/portfolios/${result.response.portfolioId}/properties/${result.response.propertyId}/units/${result.id}`;
      case 'tenant':
        return `/users/tenants/${result.id}`;
      case 'owner':
        return `/users/owners/${result.id}`;
      case 'vendor':
        return `/maintenance/vendors/${result.id}`;
      case 'tutorial':
        if (result.response.linkToTextGuide) {
          return result.response.linkToTextGuide;
        }
        return '#';
      default:
        console.error('Unknown result type:', result.type);
        return '#';
    }
  };

  const handleResultClick = async (result: SearchResult, event?: Event) => {
    if (result.type === 'tutorial') {
      await tutorialRepository
        .trackTutorialClick({
          searchQuery: searchTerm() || '',
          tutorialId: result.id,
        })
        .catch((err) => console.error('Failed to track tutorial click:', err));

      if (!result.response.linkToTextGuide && result.response.youTubeVideoId) {
        event?.preventDefault();
        setSelectedTutorial(result.response);
        setVideoModalVisible(true);
      }
    }

    setDropdownVisible(false);
    setSearchTerm('');
    props.setIsSearchActive(false);
    resetSearchResults();
    setIsFirstLoad(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalVisible(false);
    setSelectedTutorial(null);
  };

  // Icon-only circle below `lg`, where the collapsed bar shrinks to a 38px button
  // matching the nav buttons beside it.
  // `useIsMobileWidth` tracks max-width 1023px, the same boundary as Tailwind's `lg`.
  const collapsedToCircle = createMemo(() => !dropdownVisible() && isMobileWidth());

  // Sit just left of the nav's right-hand buttons. Collapsed, the bar lives in the
  // main nav row on every breakpoint, so it clears that cluster by its measured
  // width. Expanded it spans the row instead and is positioned by class.
  const clusterRightOffset = createMemo(() => {
    if (dropdownVisible()) return undefined;
    return { right: `${props.rightClusterInset + SEARCH_BAR_GAP}px` };
  });

  const hiddenBehindSidebar = createMemo(() => (menu?.isMobile() ?? false) && (menu?.isSidebarOpen() ?? false));

  return (
    <>
      <Portal>
        <div
          class="fixed z-30"
          ref={setFormElement}
          classList={{
            hidden: hiddenBehindSidebar(),
            'z-50': dropdownVisible() && !hiddenBehindSidebar(),
            'left-2 right-2 top-0 pt-2.5': isMobileWidth() && dropdownVisible(),
            'top-0 pt-2.5': isMobileWidth() && !dropdownVisible(),
            'top-0 pt-3.5': !isMobileWidth(),
            'right-4': !isMobileWidth() && dropdownVisible(),
          }}
          style={clusterRightOffset()}>
          <div
            class={`relative rounded-full transition-all duration-300 ${dropdownVisible() ? '' : 'overflow-hidden'} ${
              isMobileWidth()
                ? dropdownVisible()
                  ? 'w-full'
                  : 'ml-auto w-[38px]'
                : dropdownVisible()
                  ? 'w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] md:w-[535px] lg:w-[650px] xl:w-[850px]'
                  : 'w-[38px] lg:w-[270px] xl:w-96'
            }`}
            style={{ 'transform-origin': 'left center' }}
            onClick={() => handleFocus()}>
            <MagicSearchInput
              collapsed={collapsedToCircle()}
              searchTerm={searchTerm() || ''}
              handleSearchChange={handleSearchChange}
              handleKeyDown={handleKeyDown}
              handleFocus={handleFocus}
              handleClear={handleClear}
              dropdownVisible={dropdownVisible}
              isLoading={(isLoading() || isTyping()) && !isFirstLoad()}
            />

            <Show when={dropdownVisible()}>
              <div ref={dropdownRef}>
                <MagicSearchDropdown
                  searchResults={searchResults}
                  searchTerm={searchTerm}
                  isTyping={isTyping}
                  handleResultClick={handleResultClick}
                  isLoading={isLoading}
                  loadMore={loadMore}
                  hasMore={hasMore}
                  getResultHref={getResultHref}
                  isFirstLoad={isFirstLoad}
                />
              </div>
            </Show>
          </div>
        </div>
      </Portal>

      <Show when={selectedTutorial()}>
        {(tutorial) => (
          <YouTubeVideoModal
            visible={videoModalVisible()}
            onClose={handleCloseVideoModal}
            description={tutorial().description}
            videoId={tutorial().youTubeVideoId}
            title={tutorial().name}
          />
        )}
      </Show>
    </>
  );
};

export default SearchBar;
